<template>
  <div class="content-wrapper">
    <SendGiftMenu header="Product Details" />
    <div class="arrow-link" v-if="loadStatus">
      <router-link :to="{ name: 'GiftList' }">
        <span class="inter">Back</span>
      </router-link>
    </div>

    <!-- loader -->
    <section
      class="product-details-wrapper"
      v-if="!loadStatus && !productNotFound"
    >
      <div class="product-detail-image-wrapper">
        <div class="portrait-loader">
          <Skeleton height="80%" width="100%" />
        </div>
      </div>
      <div class="product-detail-content-wrapper">
        <div class="product-detail">
          <div class="product-detail-title loader">
            <Skeleton height="80%" width="100%" />
          </div>
          <div class="product-detail-price loader">
            <Skeleton height="40%" width="100%" />
          </div>
          <div class="product-detail-count loader">
            <Skeleton height="60%" width="100%" />
          </div>
        </div>
        <div class="variant-holder">
          <div class="loader">
            <Skeleton height="60%" width="20%" />
          </div>
          <div class="loader">
            <Skeleton height="60%" width="50%" />
          </div>
          <div class="loader">
            <Skeleton height="60%" width="20%" />
          </div>
          <div class="loader">
            <Skeleton height="60%" width="50%" />
          </div>
          <div class="loader">
            <Skeleton height="60%" width="20%" />
          </div>
          <div class="loader">
            <Skeleton height="60%" width="50%" />
          </div>
        </div>
      </div>
    </section>
    <!-- loader -->
    <!-- product details -->
    <section
      v-if="loadStatus && !productNotFound"
      class="product-details-wrapper"
    >
      <div class="product-detail-image-wrapper">
        <div class="portrait-loader">
          <img
            :src="this.variantImage"
            @error="handleBrokenImage($event)"
            class="product-detail-image"
          />
        </div>
      </div>
      <div class="product-detail-content-wrapper">
        <div class="product-detail">
          <div class="product-detail-title">
            {{
              productDetails.attributes ? productDetails.attributes.name : ''
            }}
          </div>
          <div class="product-detail-price inter">
            {{ retailPrice }}
          </div>
          <div class="product-detail-count inter">
            {{ inventoryDescription }}
            <span v-if="productDetails.attributes.expirationDate">
              - Expiration date
              {{ formatDate(productDetails.attributes.expirationDate) }}
            </span>
          </div>
        </div>

        <div class="variant-holder">
          <!-- section for product color -->
          <template v-if="variantColors.length">
            <div class="variant-details color-variant">
              <p>Color</p>

              <div class="color-holder">
                <p>Select color</p>
                <!-- <p>{{ selectedVariantColor }}</p> -->
                <div class="variant-colors">
                  <div
                    v-for="variantColor in variantColors"
                    :key="variantColor.id"
                    class="outside-box"
                    :class="[
                      {
                        active: variantColor.color === selectedVariantColor
                      },
                      {
                        'disabled-color': variantColors.length
                          ? variantColor.disabled
                          : ''
                      }
                    ]"
                    @click="selectColor(variantColor.color)"
                  >
                    <div
                      class="color-box"
                      :style="{
                        backgroundColor: variantColor.hexCode
                          ? variantColor.hexCode
                          : 'lightgray'
                      }"
                    >
                      {{ variantColor.hexCode ? '' : '?' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- section for product size -->
          <template v-if="variantSizes.length">
            <div class="variant-details">
              <p>Size</p>

              <v-select
                class="custom product-variant-select"
                :options="getSizesOptions(variantSizes)"
                :selectable="isSelectable"
                :placeholder="'Select size'"
                :clearable="false"
                :searchable="false"
                :value="selectedVariantSize"
                @input="selectSize"
              >
                <template #open-indicator="{ attributes }">
                  <span v-bind="attributes"> <Dropdown /> </span>
                </template>
              </v-select>
            </div>
          </template>

          <template v-if="variantTypes.length">
            <div class="variant-details">
              <p>Type</p>

              <v-select
                class="custom type-select product-variant-select"
                :options="getTypeOptions(variantTypes)"
                :placeholder="'Select type'"
                :clearable="false"
                :searchable="false"
                :value="selectedVariantType"
                @input="selectType"
              >
                <template #open-indicator="{ attributes }">
                  <span v-bind="attributes"> <Dropdown /> </span>
                </template>
              </v-select>
            </div>
          </template>
          <div class="cart-actions">
            <div class="cart-button" v-if="!productExistingInCart">
              <button
                class="button-gemnote button-gemnote--small"
                :class="{
                  'button-gemnote--disabled':
                    selectedVariant.inventoriesCount <= 0
                }"
                :disabled="hasInventory"
                @click="addToShoppingBag"
              >
                <span>Add to cart</span>
              </button>
            </div>
            <div
              class="cart-button"
              v-if="productExistingInCart && productExistingInCart.quantity"
            >
              <button
                class="button-gemnote button-gemnote--small counter"
                :class="{
                  'button-gemnote--disabled':
                    selectedVariant.inventoriesCount <= 0
                }"
              >
                <button @click="decreaseItem">
                  <img src="../../assets/subtract-white.svg" alt="close" />
                </button>

                <span> {{ productExistingInCart.quantity }}</span>
                <button @click="increaseItem">
                  <img src="../../assets/add-white.svg" alt="close" />
                </button>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- product details -->

    <div class="product-detail-wrapper" v-if="productNotFound">
      <h1 id="product-not-found">Product not found</h1>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Dropdown from '../Universal/Dropdown.vue'
import {
  progressStatus,
  layouts,
  scrollPageToTop,
  defaultNotFoundImage,
  handleBrokenImage,
  isCard
} from '../../helpers'
import Util from '../../mixins/Util'
import SendGiftMenu from '@/components/SendGiftMenu'

export default {
  name: 'ProductDetail',

  mixins: [Util],
  props: ['id'],
  components: {
    SendGiftMenu,
    Dropdown,
    Skeleton: () => import('@/components/Universal/Skeleton')
  },

  data() {
    return {
      selectedVariant: {
        id: null,
        name: '',
        displayName: '',
        price: 0,
        type: null,
        color: null,
        size: null,
        imageURL: null,
        inventoriesCount: 0
      },
      handleBrokenImage: handleBrokenImage,
      selectedVariantType: null,
      selectedVariantColor: null,
      selectedVariantSize: null,
      quantity: 1,
      variantImage: defaultNotFoundImage,
      attributes: {
        ref: 'openIndicator',
        role: 'presentation',
        class: 'custom'
      }
    }
  },
  created() {
    this.$store.dispatch('layout/setPageLayout', layouts.PAIGE_DASHBOARD)
    this.$store.dispatch('product/getProductDetails', this.$route.params.id)
    scrollPageToTop()
  },
  methods: {
    resetVariantSelection() {
      this.selectedVariant = {
        id: null,
        name: '',
        displayName: '',
        price: 0,
        type: null,
        color: null,
        size: null,
        imageURL: null,
        inventoriesCount: 0
      }
    },
    resetVariantAttributes(attributes = []) {
      attributes.forEach(attr => {
        this.selectedVariant[attr] = null
      })
    },
    selectType(variantType) {
      variantType = variantType.label
      if (this.selectedVariantType !== variantType) {
        this.selectedVariantType = variantType
        this.selectedVariantColor = null
        this.selectedVariantSize = null
        this.resetVariantAttributes(['color', 'size', 'inventoriesCount'])
        // get images for first product variant with same color picked
        let filteredVariantImages = this.productVariantsImages.filter(
          variant => variant.spec.type === variantType
        )
        this.getVariantImage(filteredVariantImages)
        if (this.variantColors.length) {
          this.availableColorsByType(variantType)
          let variantSizesNewState = this.variantSizes.map(variantSize => ({
            size: variantSize.size,
            disabled: true
          }))
          this.$store.dispatch(
            'product/updateVariantSizes',
            variantSizesNewState
          )
        } else if (this.variantSizes.length) {
          this.availableSizesByType(variantType)
        } else {
          this.setSelectedVariant()
        }
      }
    },
    availableColorsByType(variantType) {
      let availableColorsByType = this.variantDictionary.filter(
        variant => variant.type == variantType
      )
      let variantColorsNewState = this.variantColors
      for (let index = 0; index < variantColorsNewState.length; index++) {
        let foundColor = availableColorsByType.find(
          availableColor =>
            availableColor.color == variantColorsNewState[index].color
        )
        if (foundColor) {
          variantColorsNewState[index].disabled = false
        } else {
          variantColorsNewState[index].disabled = true
        }
      }
      this.$store.dispatch('product/updateVariantColors', variantColorsNewState)
    },

    selectColor(variantColor) {
      if (this.selectedVariantColor !== variantColor) {
        this.selectedVariantColor = variantColor
        this.selectedVariantSize = null
        this.resetVariantAttributes(['size', 'inventoriesCount'])
        // get images for first product variant with the selected color
        let filteredVariantImages = this.productVariantsImages.filter(
          variant => variant.spec.color === variantColor
        )
        this.getVariantImage(filteredVariantImages)
        this.availableSizesByColor(variantColor)
      }
    },
    availableSizesByType(variantType) {
      let availableSizes = this.variantDictionary.filter(
        variant => variant.type === variantType
      )
      let variantSizesNewState = this.variantSizes
      for (let index = 0; index < variantSizesNewState.length; index++) {
        let foundSize = availableSizes.find(
          availableSize =>
            availableSize.size == variantSizesNewState[index].size
        )
        if (foundSize) {
          variantSizesNewState[index].disabled = false
        } else {
          variantSizesNewState[index].disabled = true
        }
      }
      this.$store.dispatch('product/updateVariantSizes', variantSizesNewState)
    },
    availableSizesByColor(variantColor) {
      let availableSizes = this.variantDictionary.filter(
        variant =>
          variant.color === variantColor &&
          variant.type === this.selectedVariantType
      )
      if (this.variantSizes.length) {
        let variantSizesNewState = this.variantSizes
        for (let index = 0; index < variantSizesNewState.length; index++) {
          let foundSize = availableSizes.find(
            availableSize =>
              availableSize.size == variantSizesNewState[index].size
          )
          if (foundSize) {
            variantSizesNewState[index].disabled = false
          } else {
            variantSizesNewState[index].disabled = true
          }
        }
        this.$store.dispatch('product/updateVariantSizes', variantSizesNewState)
      } else {
        this.setSelectedVariant()
      }
    },
    selectSize(variantSize) {
      variantSize = variantSize.label
      this.selectedVariantSize = variantSize
      this.setSelectedVariant()
    },
    setSelectedVariant() {
      let foundVariant = []

      this.productVariants.map(variant => {
        if (
          variant.attributes.spec.type === this.selectedVariantType &&
          variant.attributes.spec.color === this.selectedVariantColor &&
          variant.attributes.spec.size === this.selectedVariantSize
        ) {
          foundVariant.push({
            id: variant.id,
            attributes: variant.attributes
          })
        }
      })

      if (foundVariant.length) {
        this.selectedVariant.id = parseInt(foundVariant[0].id)
        this.selectedVariant.name = foundVariant[0].attributes.name
        this.selectedVariant.size = foundVariant[0].attributes.spec.size
          ? foundVariant[0].attributes.spec.size
          : ''
        this.selectedVariant.type = foundVariant[0].attributes.spec.type
          ? foundVariant[0].attributes.spec.type
          : null
        this.selectedVariant.color = foundVariant[0].attributes.spec.color
          ? foundVariant[0].attributes.spec.color
          : ''
        this.selectedVariant.displayName = this.productName
        this.selectedVariant.price = foundVariant[0].attributes.price
        this.selectedVariant.imageURL =
          foundVariant[0].attributes.representativeImages.length > 0
            ? foundVariant[0].attributes.representativeImages[0].url
            : null
        this.selectedVariant.inventoriesCount =
          foundVariant[0].attributes.inventoriesCount
      }
    },
    addToShoppingBag() {
      let errors = []
      if (this.selectedVariant.id === null) {
        errors.push('Please select a product variant')
      }
      if (this.selectedVariant.price === null) {
        errors.push(`Can't add product without price`)
      }
      if (!this.quantity) {
        errors.push('Please select product quantity')
      }
      if (this.quantity > this.selectedVariant.inventoriesCount) {
        errors.push(`Max quantity reached for this item`)
      }
      if (errors.length) {
        this.$store.dispatch('notification/pushNotification', {
          type: 'error',
          messages: errors
        })
        return
      }
      const product = {
        id: this.productDetails.id,
        selectedVariant: this.selectedVariant,
        quantity: parseInt(this.quantity),
        attributes: this.productDetails.attributes,
        type: 'product'
      }

      if (this.productDetails.attributes.expirationDate) {
        const expirationDate = new Date(
          this.productDetails.attributes.expirationDate
        )
        const currentDate = new Date()

        // Calculate date 30 days from now
        const thirtyDaysFromNow = new Date()
        thirtyDaysFromNow.setDate(currentDate.getDate() + 30)

        if (expirationDate < currentDate) {
          this.$store.dispatch('notification/pushNotification', {
            title: this.productDetails.attributes.name,
            type: 'warning',
            messages: [
              `This product has a limited shelf life and expired on ${this.formatDate(
                expirationDate
              )}. Please exercise discretion when placing an order.`
            ]
          })
        } else if (expirationDate <= thirtyDaysFromNow) {
          this.$store.dispatch('notification/pushNotification', {
            title: this.productDetails.attributes.name,
            type: 'warning',
            messages: [
              `This product has a limited shelf life and will expire on ${this.formatDate(
                expirationDate
              )}. Please exercise discretion when placing an order.`
            ]
          })
        } else {
          this.$store.dispatch('order/addProductToShoppingBag', product)
        }
      } else {
        this.$store.dispatch('order/addProductToShoppingBag', product)
      }
    },
    getTypeOptions(variantTypes) {
      let options = []
      for (let index = 0; index < variantTypes.length; index++) {
        const variantType = variantTypes[index]
        options.push({
          code: parseInt(variantType.id),
          label: variantType.type
        })
      }
      return options.sort()
    },
    getSizesOptions(variantSizes) {
      let options = []
      for (let index = 0; index < variantSizes.length; index++) {
        const variantSize = variantSizes[index]
        if (!variantSize.disabled) {
          options.push({
            code: parseInt(variantSize.id),
            label: variantSize.size,
            disabled: variantSize.disabled
          })
        }
      }
      return options.sort()
    },
    isSelectable(value) {
      if (!value.disabled) {
        return true
      }
      return false
    },
    getVariantImage(filteredVariantImages) {
      this.variantImage = filteredVariantImages.length
        ? filteredVariantImages[0].images[0].url
        : this.productDetails.attributes.defaultProductVariantImages.length
        ? this.productDetails.attributes.defaultProductVariantImages[0].url
        : defaultNotFoundImage
    },
    increaseItem() {
      if (this.productExistingInCart) {
        if (this.productExistingInCart.type == 'bundle') {
          return this.$store.dispatch(
            'order/increaseBundleItem',
            this.productExistingInCart
          )
        }

        this.$store.dispatch(
          'order/increaseShoppingBagItem',
          this.productExistingInCart
        )
      }
    },
    decreaseItem() {
      if (this.productExistingInCart) {
        if (this.productExistingInCart.quantity === 1) {
          this.removeItem(this.productExistingInCart)
        } else {
          this.$store.dispatch(
            'order/decreaseShoppingBagItem',
            this.productExistingInCart
          )
        }
      }
    },
    removeItem(product) {
      if (product.attributes && isCard(product.attributes.tagList)) {
        localStorage.removeItem('selectedCard')
      }
      this.$store.dispatch('order/removeShoppingBagItem', product)
    }
  },
  watch: {
    defaultSelection(defaultSelection) {
      let productVariant = this.$route.query.productVariant
      let filteredVariantImages = null
      let availableSizesByColor = []
      if (!productVariant) {
        switch (defaultSelection.defaultBy) {
          case 'type':
            this.selectedVariantType = defaultSelection.default.type
            this.selectedVariantColor = null
            this.selectedVariantSize = null
            // get images for first product variant with same color picked
            filteredVariantImages = this.productVariantsImages.filter(
              variant => variant.spec.type === defaultSelection.default.type
            )
            this.getVariantImage(filteredVariantImages)
            // if colors array exist
            if (this.variantColors.length) {
              this.availableColorsByType(defaultSelection.default.type)
              // search for available color by type
              let availableColorsByType = this.variantDictionary.filter(
                variant => variant.type == defaultSelection.default.type
              )
              if (availableColorsByType.length) {
                this.selectColor(availableColorsByType[0].color)
              }
              if (availableColorsByType.length) {
                let availableSizesByColor = this.variantDictionary.filter(
                  variant =>
                    variant.color === availableColorsByType[0].color &&
                    variant.type === defaultSelection.default.type
                )
                if (availableSizesByColor.length) {
                  this.selectSize({ label: availableSizesByColor[0].size })
                }
              } else {
                // search for available color by type
                let availableSizesByType = this.variantDictionary.filter(
                  variant => variant.type === defaultSelection.default.type
                )
                if (availableSizesByType.length) {
                  this.selectSize({ label: availableSizesByType[0].size })
                }
              }
            } else if (this.variantSizes.length) {
              // if sizes array exist
              this.availableSizesByType(defaultSelection.default.type)
              // search for available color by type
              let availableSizesByType = this.variantDictionary.filter(
                variant => variant.type === defaultSelection.default.type
              )
              if (availableSizesByType.length) {
                this.selectSize({ label: availableSizesByType[0].size })
              }
            } else {
              // preselect bby default if there is only types array
              this.setSelectedVariant()
            }
            break
          case 'color':
            this.selectedVariantType = null
            this.selectedVariantColor = defaultSelection.default.color
            this.selectedVariantSize = null

            // get images for first product variant with same color picked
            filteredVariantImages = this.productVariantsImages.filter(
              variant => variant.spec.color === defaultSelection.default.color
            )
            this.getVariantImage(filteredVariantImages)
            this.availableSizesByColor(defaultSelection.default.color)
            // search for available sizes by color
            availableSizesByColor = this.variantDictionary.filter(
              variant =>
                variant.color === defaultSelection.default.color &&
                variant.type === null
            )
            if (availableSizesByColor.length) {
              this.selectSize({ label: availableSizesByColor[0].size })
            }
            break
          case 'size':
            this.selectedVariantType = null
            this.selectedVariantColor = null
            this.selectedVariantSize = null


            if(defaultSelection.default){
              this.selectedVariantSize = defaultSelection.default.size
             // get images for first product variant with same color picked
              filteredVariantImages = this.productVariantsImages.filter(
                variant => variant.spec.size === defaultSelection.default.size
              )
              this.getVariantImage(filteredVariantImages)
            }
            this.setSelectedVariant()
            break
        }
      } else {
        productVariant = JSON.parse(productVariant)
        this.selectedVariantType = productVariant.type
        this.selectedVariantColor = productVariant.color
        this.selectedVariantSize = productVariant.size
        this.quantity = this.$route.query.quantity
        this.selectedVariant = { ...productVariant }
        filteredVariantImages = this.productVariantsImages.filter(
          variant => variant.spec.type === productVariant.type
        )
        this.getVariantImage(filteredVariantImages)
        if (productVariant.type) this.availableColorsByType(productVariant.type)
        if (productVariant.color)
          this.availableSizesByColor(productVariant.color)
      }
    }
  },
  computed: {
    loadStatus() {
      return progressStatus(this.loadProductDetails)
    },
    hasInventory() {
      return this.selectedVariant.inventoriesCount <= 0
    },
    generateQuantityList() {
      let quantities = []
      if (this.selectedVariant.inventoriesCount > 0) {
        for (let q = 1; q <= this.selectedVariant.inventoriesCount; q++) {
          quantities.push(q)
        }
        return quantities
      } else {
        return quantities
      }
    },
    inventoryDescription() {
      return ` ${this.selectedVariant.inventoriesCount || 0} in stock`
    },
    retailPrice() {
      let value = ''
      if (this.selectedVariant.id === null) {
        let prices = this.productVariants.map(
          variant => variant.attributes.price
        )
        if (prices.length) {
          let minumum = Math.min(...prices)
          let maximum = Math.max(...prices)
          if (minumum === maximum) {
            if (!Number.isNaN(maximum)) {
              return this.formatCurrency(maximum)
            }
          } else {
            return (value = `${this.formatCurrency(
              minumum
            )} - ${this.formatCurrency(maximum)}`)
          }
        }
      } else {
        if (!Number.isNaN(this.selectedVariant.price)) {
          return (value = this.formatCurrency(this.selectedVariant.price))
        }
      }

      return value || this.formatCurrency(0)
    },
    productName() {
      return this.productDetails && this.productDetails.attributes
        ? this.productDetails.attributes.name
        : ''
    },
    showPrices() {
      return this.$store.getters['team/showPrices']
    },
    productExistingInCart() {
      if (!this.shoppingBagProducts) {
        return null
      }
      const productVariants = this.shoppingBagProducts.filter(
        product => product.id === this.id
      )
      if (!productVariants.length) {
        return null
      }
      for (const productVariant of productVariants) {
        if (productVariant.selectedVariant.id === this.selectedVariant.id) {
          return productVariant
        }
      }
      return null
    },
    ...mapState({
      loadProductDetails: state => state.product.loadProductDetails,
      productDetails: state => state.product.productDetails,
      productVariants: state => state.product.productVariants,
      productVariantsImages: state => state.product.productVariantsImages,
      variantTypes: state => state.product.variantTypes,
      variantDictionary: state => state.product.variantDictionary,
      variantColors: state => state.product.variantColors,
      variantSizes: state => state.product.variantSizes,
      defaultSelection: state => state.product.defaultSelection,
      productNotFound: state => state.product.productNotFound,
      shoppingBagProducts: state => state.order.shoppingBagProducts
    })
  }
}
</script>

<style lang="scss" scoped>
@import '@/global.scss';

.product-details {
  text-align: left;
  margin: 1.5rem 0;

  h2 {
    padding-bottom: 0.25rem;
    font-size: 2rem;
    font-weight: 600;
    color: $main-6;
    line-height: 44px;
  }

  p {
    font-weight: normal;
  }
}

.product-details-wrapper {
  margin-top: 2.5rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-radius: 10px;
  border: 1px solid #232220;
  min-height: 550px;

  .product-detail-image-wrapper {
    background-color: #f9f9f9;
    margin: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    .portrait-loader {
      width: 80%;
      height: 90%;
      margin: 0px auto;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
  }
  .product-detail-content-wrapper {
    border-left: 1px solid #232220;
    text-align: left;

    .product-detail {
      border-bottom: 1px solid #232220;
      padding: 16px 12px 12px 20px;

      .product-detail-title {
        text-transform: capitalize;
        font-family: $editorial;
        font-size: 2rem;
        line-height: 40px;
        width: 80%;

        &.loader {
          margin-top: 1rem;
          height: 50px;
        }
      }

      .product-detail-price {
        margin: 0.5rem 0;
        font-size: 1.363rem;
        font-weight: 600;
        color: #24292c;
        &.loader {
          margin-top: 1rem;
          height: 45px;
          width: 30%;
        }
      }

      .product-detail-count {
        padding-top: 0.5rem;
        font-size: 0.938rem;
        color: #232220;
        &.loader {
          padding-top: 0rem;
          height: 40px;
          width: 60%;
        }
      }
    }
  }
}

@media (max-width: 575.98px) {
  .product-details-wrapper {
    grid-template-columns: 1fr;

    .product-detail-image-wrapper {
      min-height: 400px;
    }

    .product-detail-content-wrapper {
      border-left: none;
      border-top: 1px solid #232220;

      .product-detail {
        .product-detail-title {
          width: 100%;
        }
      }
    }
  }
}

.product-detail-wrapper {
  margin: 0 auto;
  height: auto;
  width: 33%;
}

.portrait {
  border: 1px solid $gray-variant-2;
  height: 296px;
  margin: 0 auto;
  padding: 2rem;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    margin: 0 auto;
  }
}

.product-info {
  margin: 0.75rem 0;
  .product-name {
    font-size: 0.875rem;
    font-weight: normal;
    text-align: center;
  }
}

.variant-holder {
  margin: 2rem 1rem;
  padding-left: 20px;
  margin-left: 0;

  .loader {
    height: 40px;
    margin-bottom: 1rem;
  }
  .variant-details {
    display: flex;
    flex-direction: column;
    margin-bottom: 2rem;
    margin-top: 1rem;
    width: 60%;

    &.color-variant {
      width: 90%;
      margin-top: 2rem;
    }

    .gift-select {
      width: 100%;
    }

    .color-holder {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 1rem;

      p {
        width: 100%;
        font-family: $inter;
        font-size: 0.938rem;
        font-weight: 400;
        width: max-content;
      }
    }

    p {
      width: 35%;
      font-family: $inter;
      font-size: 0.938rem;
      text-align: left;
      font-weight: 700;

      span {
        font-family: $inter;
        font-weight: 600;
        font-size: 0.813rem;
      }
    }
  }
}

.variant-type {
  display: flex;
  align-items: center;
  padding: 0;
  margin-bottom: 1.5rem;
  gap: 1.5rem;
  span {
    flex-basis: 20%;
    text-align: left;
  }
  div {
    flex: 1;
  }
  // li:first-child {
  //   margin-left: 0;
  // }
  // li {
  //   cursor: pointer;
  //   margin: 0.2rem 0.2rem;
  //   font-size: 16px;
  //   line-height: 20px;
  //   padding: 0.2rem 0.5rem;
  //   border-radius: 0px;
  //   background-color: transparent;
  // }
}

.variant-colors {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0;

  .outside-box {
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid transparent;
    box-sizing: content-box;
    // box-shadow: 0 0 2px grey !important;
  }
  .outside-box.active {
    border: 1px solid #000;
  }
  .color-box {
    color: $tab-ative;
    font-size: 0.875rem;
    cursor: pointer;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
.disabled-color {
  pointer-events: none;
  opacity: 0.2;
}
.disabled-size {
  pointer-events: none;
  opacity: 0.2;
}

.cart-actions {
  margin: 1.5rem 0rem;
  display: flex;
  flex-direction: column;
  // grid-template-columns: repeat(auto-fill, minmax(50%, 1fr));
  // grid-template-rows: auto;
  // align-items: center;

  .cart-button {
    display: flex;
    margin-top: 0.5rem;
    justify-content: flex-start;

    .button-gemnote--small {
      font-weight: normal;
      width: 11.125rem;

      &.counter {
        width: 8.125rem;
        gap: 0.5rem;

        &:hover {
          background-color: #232220 !important;
          opacity: 1 !important;
        }
      }
    }
  }
}
.selected-size {
  background-color: transparent;
  border: 0;
  border-bottom: 1px solid #25282a;
  color: $onyx;
  font-weight: 700;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
@media screen and (max-width: 1399px) and (min-width: 660px) {
  .product-detail-wrapper {
    width: 60%;
  }
}
@media screen and (max-width: 659px) and (min-width: 500px) {
  .product-detail-wrapper {
    width: 80%;
    padding: 0;
  }
}
@media screen and (max-width: 499px) and (min-width: 350px) {
  .product-detail-wrapper {
    width: 100%;
    padding: 0;
  }
}
</style>
